import VaultInput from "shared-components/vault-input";
import NavButtons from "../components/nav-buttons";
import { Page } from "../types";
import { SnapSelectMenu } from "suit";
import { BUSINESS_STRUCTURES, BUSINESS_TYPES, STATES } from "../const";
import { useState } from "react";
import Address from "../components/address";
import FormPage from "../components/form-page";

type OrganizationPageProps = {
  visible: boolean;
  setCurrentPage: (page: Page | undefined) => void;
  onKybTypeChange: (type: string) => void;
  onKybStructureChange: (type: string) => void;
  onKybStateChange: (state: string) => void;
  onKybStateOfIncorporationChange: (state: string) => void;
};

export default function OrganizationPage({
  visible,
  setCurrentPage,
  onKybStructureChange,
  onKybTypeChange,
  onKybStateChange,
  onKybStateOfIncorporationChange,
}: OrganizationPageProps) {
  const [kybType, setKybType] = useState(BUSINESS_TYPES[0]);
  const [legalNameIsValid, setLegalNameIsValid] = useState(false);
  const [customerFacingNameIsValid, setCustomerFacingNameIsValid] =
    useState(false);
  const [emailIsValid, setEmailIsValid] = useState(false);
  const [urlIsValid, setUrlIsValid] = useState(false);
  const [phoneIsValid, setPhoneIsValid] = useState(false);
  const [einIsValid, setEinIsValid] = useState(false);
  const [addressIsValid, setAddressIsValid] = useState(false);
  const formIsValid =
    legalNameIsValid &&
    customerFacingNameIsValid &&
    emailIsValid &&
    urlIsValid &&
    phoneIsValid &&
    einIsValid &&
    addressIsValid;

  return (
    <FormPage visible={visible}>
      <h2 className="text-md lg:text-xl font-semibold">Organization Data</h2>
      <p>
        Our payment partner requires us to collect and verify certain
        information about our customers. Should the information be unverifiable,
        we may require additional documentation. Protecting your information is
        our priority.
      </p>
      <p className="text-sm my-4">
        <span className="text-red-600">*</span> Indicates a required field.
      </p>
      <div className="lg:grid grid-cols-2 lg:gap-6 flex flex-col lg:mt-4">
        <div className="w-full">
          <VaultInput
            name="kybLegalName"
            type="text"
            placeholder="Legal Name"
            label="Legal Name"
            onValid={setLegalNameIsValid}
            required={true}
          />
        </div>
        <div className="w-full">
          <VaultInput
            name="kybCustomerFacingName"
            type="text"
            placeholder="Customer Facing Name"
            label="Customer Facing Name"
            onValid={setCustomerFacingNameIsValid}
            required={true}
          />
        </div>
      </div>
      <div className="lg:grid grid-cols-2 lg:gap-6 flex flex-col lg:mt-4">
        <div className="w-full">
          <SnapSelectMenu
            placeholder="Business Type"
            label="Business Type"
            selectMenuOptions={BUSINESS_TYPES}
            onSnap-select-menu-updated={(e) => {
              const selected = e.detail.find((option) => option.selected);
              if (selected?.value) {
                setKybType(selected);
                onKybTypeChange(selected.value);
              }
            }}
          />
        </div>
        <div className="w-full">
          <SnapSelectMenu
            placeholder="Business Structure"
            label="Business Structure"
            selectMenuOptions={BUSINESS_STRUCTURES[kybType.value || ""]}
            onSnap-select-menu-updated={(e) => {
              const selected = e.detail.find((option) => option.selected);
              if (selected?.value) {
                onKybStructureChange(selected.value);
              }
            }}
          />
        </div>
      </div>
      <div className="lg:grid grid-cols-2 lg:gap-6 flex flex-col lg:mt-4">
        <div className="w-full">
          <SnapSelectMenu
            placeholder="State of Incorporation"
            label="State of Incorporation"
            selectMenuOptions={STATES}
            onSnap-select-menu-updated={(e) => {
              const selected = e.detail.find((option) => option.selected);
              if (selected?.value) {
                onKybStateOfIncorporationChange(selected.value);
              }
            }}
          />
        </div>
        <div className="w-full">
          <VaultInput
            name="kybYearOfIncorporation"
            type="year"
            placeholder="Year of Incorporation"
            label="Year of Incorporation"
            onValid={setEmailIsValid}
            required={true}
          />
        </div>
      </div>
      <div className="lg:grid grid-cols-2 lg:gap-6 flex flex-col lg:mt-4">
        <div className="w-full">
          <VaultInput
            name="kybEmail"
            type="email"
            placeholder="Email"
            label="Email"
            onValid={setEmailIsValid}
            required={true}
          />
        </div>
        <div className="w-full">
          <VaultInput
            name="kybUrl"
            type="url"
            placeholder="URL"
            label="URL"
            onValid={setUrlIsValid}
            required={true}
          />
        </div>
      </div>
      <div className="lg:grid grid-cols-2 lg:gap-6 flex flex-col lg:mt-4">
        <div className="w-full">
          <VaultInput
            name="kybPhone"
            type="phone"
            placeholder="Phone Number"
            label="Phone Number"
            onValid={setPhoneIsValid}
            required={true}
          />
        </div>
        <div className="w-full">
          <VaultInput
            name="kybEin"
            type="ein"
            placeholder="EIN"
            label="EIN"
            onValid={setEinIsValid}
            required={true}
          />
        </div>
      </div>
      <Address
        entityPrefix="kyb"
        onValid={setAddressIsValid}
        onStateSelect={onKybStateChange}
      />
      <NavButtons
        onNextClick={() => setCurrentPage("officer")}
        nextButtonDisabled={!formIsValid}
      />
    </FormPage>
  );
}
